import type { DocNode } from '@atlaskit/adf-schema/doc';

// Default delay between stream completion and the retention measurement.
// Tunable at runtime via the RETENTION_DELAY_CONFIG_KEY Statsig dynamic
// config; this is the fallback.
export const RETENTION_MEASUREMENT_DELAY_MS = 30_000;

// Statsig dynamic config holding the post-stream measurement window in ms.
export const RETENTION_DELAY_CONFIG_KEY = 'aifc_content_retention_delay_ms';

// Fraction of the streamed content that must still be present for the content
// to count as "retained". Additions and in-place edits keep the ratio high;
// only removal (undo / delete / clearing the page) collapses it.
export const RETENTION_THRESHOLD = 0.5;

export type ADFNodeLike = {
	type?: string;
	text?: string;
	content?: ADFNodeLike[];
};

/**
 * Flatten an ADF document into a single plain-text string. Block boundaries are
 * joined with a space so adjacent words don't merge into spurious tokens.
 *
 * Note: the returned text is used only for local retention math and must never
 * be emitted in analytics (it is user-generated content).
 */
export function extractPlainText(node: ADFNodeLike | DocNode | null | undefined): string {
	if (!node) {
		return '';
	}

	const adfNode = node as ADFNodeLike;
	if (adfNode.type === 'text') {
		return adfNode.text ?? '';
	}
	const children = adfNode.content ?? [];
	return children.map(extractPlainText).join(' ');
}

function tokenize(text: string): string[] {
	return text.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? [];
}

/**
 * Fraction of the original (streamed) content's distinct word tokens still
 * present in the current document.
 *
 * - Additions introduce new tokens that are ignored, so they don't lower the ratio.
 * - In-place edits that keep most words stay near 1.
 * - Undo / delete / clearing removes the original tokens, driving the ratio to 0.
 */
export function computeRetentionRatio(originalText: string, currentText: string): number {
	const originalTokens = tokenize(originalText);
	if (originalTokens.length === 0) {
		// Nothing meaningful streamed in; treat any remaining content as retained.
		return tokenize(currentText).length > 0 ? 1 : 0;
	}
	const originalSet = new Set(originalTokens);
	const currentSet = new Set(tokenize(currentText));
	let retained = 0;
	originalSet.forEach((token) => {
		if (currentSet.has(token)) {
			retained += 1;
		}
	});
	return retained / originalSet.size;
}

export type RetentionAttribution = {
	singleInstrumentationId?: string;
	entryPoint?: string;
	conversationChannelId?: string;
};

const readStringField = (payload: Record<string, unknown>, key: string): string | undefined => {
	const value = payload[key];
	return typeof value === 'string' ? value : undefined;
};

/**
 * Reads the retention attribution fields from a create stream payload.
 */
export function getRetentionAttribution(payload: Record<string, unknown>): RetentionAttribution {
	return {
		singleInstrumentationId: readStringField(payload, 'singleInstrumentationId'),
		entryPoint: readStringField(payload, 'entryPoint'),
		conversationChannelId: readStringField(payload, 'conversationChannelId'),
	};
}
